import React, { useCallback, useMemo, useState } from 'react'
import { RouteComponentProps } from 'wouter'

import { useStoreState } from '../../../hooks/state'
import { Category } from '../../../state/models/audio'
import { graphql } from '../../../services/graphql'
import { serviceModals } from '../../../services/modals'

import { buildComponent } from '../../../components/factory'

import * as UI from '@mantine/core'
import * as Icons from '../../../components/icons'
import * as Routing from 'wouter'

import { Table } from './components/table'

export const ScreenConnectedCategories = buildComponent<RouteComponentProps>()
  .withLifecycle(() => {
    const [search, setSearch] = useState('')
    const [typeFilter, setTypeFilter] = useState<
      'all' | 'theme' | 'book' | 'background'
    >('all')
    const [_selection, setSelection] = useState<string[]>([])

    const allCategories = useStoreState((store) => store.audio.categories)

    const selection = useMemo(
      () =>
        _selection.filter((id) =>
          allCategories.some((cat: Category) => cat._id === id),
        ),
      [_selection, allCategories],
    )

    const compare = (...terms: (string | undefined)[]) => {
      return terms.some((t) => t?.toLowerCase().includes(search.toLowerCase()))
    }

    const categories = allCategories
      .filter((c: Category) =>
        compare(c.short?.fr, c.short?.en, c.long?.fr, c.long?.en, c.slug),
      )
      .filter((c: Category) => typeFilter === 'all' || c.type === typeFilter)

    const toggleRow = useCallback(
      (id: string) =>
        setSelection((current) =>
          current.includes(id)
            ? current.filter((item) => item !== id)
            : [...current, id],
        ),
      [],
    )

    const toggleAll = useCallback(
      () =>
        setSelection((current) =>
          current.length === categories.length
            ? []
            : categories.map((item: Category) => item._id),
        ),
      [categories],
    )

    const isSelected = useCallback(
      (id: string) => selection.includes(id),
      [selection],
    )

    const deleteSelected = useCallback(async () => {
      try {
        await serviceModals.confirmDeletion(
          `Supprimer ${selection.length} catégorie${
            selection.length > 1 ? 's' : ''
          }`,
          `Voulez-vous vraiment supprimer ce${
            selection.length > 1 ? 's' : 'tte'
          } ${selection.length} catégorie${selection.length > 1 ? 's' : ''} ?`,
        )

        await Promise.all(
          selection.map((categoryId) =>
            graphql.mutations.deleteCategory(categoryId),
          ),
        )

        setSelection([])
      } catch (err) {
        console.error('Erreur lors de la suppression:', err)
      }
    }, [selection])

    return {
      typeFilter,
      setTypeFilter,
      search,
      setSearch,
      categories,
      selection,
      toggleAll,
      toggleRow,
      isSelected,
      deleteSelected,
    }
  })
  .withRender(({ lifecycle }) => (
    <React.Fragment>
      <UI.Stack
        mx={-16}
        px={16}
        mt={-16}
        py={16}
        sx={{
          position: 'sticky',
          top: 72,
          zIndex: 10,
          backgroundColor: '#f8f9fc',
        }}
        spacing={0}
      >
        <UI.Group position="apart">
          <UI.Stack spacing={0}>
            <UI.Title>
              <UI.Text size={22}>Catégories</UI.Text>
            </UI.Title>
            <UI.Text color="dimmed">
              Configuration des catégories pour organiser les contenus audio.
            </UI.Text>
          </UI.Stack>
        </UI.Group>

        <UI.Group mx="auto" mt="lg" position="center" spacing={0}>
          <UI.SegmentedControl
            radius="xl"
            value={lifecycle.typeFilter}
            onChange={(value) =>
              lifecycle.setTypeFilter(value as 'theme' | 'book' | 'background')
            }
            data={[
              { label: 'Toutes', value: 'all' },
              { label: 'Thématiques', value: 'theme' },
              { label: 'Audiobooks', value: 'book' },
              { label: 'Fonds sonores', value: 'background' },
            ]}
            styles={(theme) => ({
              root: {
                backgroundColor: 'transparent',
              },
              active: {
                backgroundColor: theme.colors.blue[7],
              },
              label: {
                padding: '1rem 2rem',
              },
              labelActive: {
                color: 'white !important',
              },
            })}
          />
        </UI.Group>

        <UI.Group mx="auto" mt="lg" position="center" spacing={8}>
          <UI.TextInput
            mx={8}
            sx={{ width: 440 }}
            value={lifecycle.search}
            onChange={(e) => lifecycle.setSearch(e.target.value)}
            placeholder="Chercher une catégorie"
            variant="default"
            rightSection={
              <Icons.FontAwesomeIcon
                color="#AAAAAA"
                icon={Icons.Solid.faSearch}
                style={{ marginRight: '2rem' }}
              />
            }
          />
          <Routing.Link to="/categories/create">
            <UI.Button
              size="sm"
              sx={{ minHeight: 38 }}
              mb={1}
              px="4rem"
              component="a"
              leftIcon={<Icons.FontAwesomeIcon icon={Icons.Solid.faPlus} />}
            >
              Ajouter une catégorie
            </UI.Button>
          </Routing.Link>
        </UI.Group>
      </UI.Stack>

      <UI.Group
        sx={{
          height: '7rem',
          position: 'sticky',
          top: 231,
          marginBottom: -64,
          backgroundColor: 'white',
          zIndex: 10,
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          borderRadius: '6px 6px 0 0',
        }}
        px={16}
      >
        <UI.Group
          align="center"
          position="center"
          style={{ width: '4rem', flex: 'none' }}
        >
          <UI.Checkbox
            checked={lifecycle.selection.length === lifecycle.categories.length}
            indeterminate={
              lifecycle.selection.length > 0 &&
              lifecycle.selection.length !== lifecycle.categories.length
            }
            onChange={lifecycle.toggleAll}
            transitionDuration={0}
          />
        </UI.Group>
        <div style={{ width: '20rem', flex: 'none' }}>
          <UI.Text weight={700}>Catégorie</UI.Text>
        </div>
        <div style={{ width: '20rem', flex: 'none' }}>
          <UI.Text weight={700}>Sous-catégorie</UI.Text>
        </div>
        <div style={{ flex: 1 }}>
          <UI.Text weight={700}>Type</UI.Text>
        </div>

        <UI.Text style={{ position: 'relative' }}>
          {!!lifecycle.selection.length && (
            <UI.Button
              color="danger"
              size="sm"
              sx={{
                minHeight: 30,
                position: 'absolute',
                right: 0,
                top: -20,
              }}
              onClick={lifecycle.deleteSelected}
            >
              Supprimer {lifecycle.selection.length} catégorie
              {lifecycle.selection.length > 1 ? 's' : ''}
            </UI.Button>
          )}
        </UI.Text>
      </UI.Group>

      <UI.Card pt={56} shadow="sm" mt={8} p={0}>
        <Table
          data={lifecycle.categories}
          toggleRow={lifecycle.toggleRow}
          isSelected={lifecycle.isSelected}
        />
      </UI.Card>
    </React.Fragment>
  ))
